import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import { Textarea } from '@/shared/components/ui/textarea';
import { quickTaskDraftSchema } from '../schemas';
import type { QuickTaskDraftFormValues } from '../schemas';
import type { QuickTaskProposal } from '../types';
import { useProjectStatuses } from '@/features/tasks/public';
import { useTags } from '@/features/tags/public';
import type { Tag as ProjectTag } from '@/features/tags/public';
import { proposalToFormValues } from '../utils/proposalToFormValues';
import type { TaskFieldOptions } from './TaskFieldEditor';
import { ProposalChipRow } from './ProposalChipRow';
import { ProposalNewEntityWarning } from './ProposalNewEntityWarning';

export type { TaskFieldOptions } from './TaskFieldEditor';

interface AiProposalPreviewProps {
  proposal: QuickTaskProposal;
  options: TaskFieldOptions;
  /** The board the proposal targets — the status and tag pickers create into it. */
  projectId: string;
  boardId: string;
  onConfirm: (draft: QuickTaskDraftFormValues, isEdited: boolean) => void;
}

/**
 * The design's *La IA entendió esto* panel: a tinted rounded box holding the
 * proposed title as editable text and every interpreted field as a pill.
 *
 * The panel renders contract state and never infers it — provenance, candidates
 * and new-entity markers each come from the response (AMF-3), so a field the
 * model could not determine shows as empty rather than as a plausible guess.
 */
export function AiProposalPreview({
  proposal,
  options,
  projectId,
  boardId,
  onConfirm,
}: AiProposalPreviewProps) {
  const { t } = useTranslation('ai');
  const { statuses: projectStatuses } = useProjectStatuses(boardId);

  // TagPicker works with Tag objects while the draft holds ids — the form field stays
  // the source of truth for what gets created, these only back the chips.
  //
  // `null` until the user touches the picker, so the proposed tag shows as selected:
  // starting at `[]` meant the picker rendered nothing while the draft already carried
  // the tag, and the first edit replaced that list — silently dropping what the AI proposed.
  const [editedTags, setEditedTags] = useState<ProjectTag[] | null>(null);
  const { data: projectTags } = useTags(projectId);

  const form = useForm<QuickTaskDraftFormValues>({
    resolver: zodResolver(quickTaskDraftSchema),
    // `values` (not defaultValues + useEffect reset): RHF syncs Controller
    // fields from external data on render. useEffect reset loses the value
    // for Controller + Radix Select fields (re-registration overwrites it).
    values: proposalToFormValues(proposal),
  });

  // The proposed tags as the picker wants them: the project's own object when it has one, and a
  // name with no id when the tag is still to be created — which is exactly what `Tag.id: null`
  // means. Filtering the project's list instead showed only the tags that already existed, so
  // touching the picker dropped every new one the proposal had named.
  const proposedTags: ProjectTag[] = proposal.tags.map(
    (ref) =>
      (projectTags?.items ?? []).find((tag) => tag.id === ref.id) ?? {
        id: ref.id,
        projectId: null,
        name: ref.name,
        // The colour the proposal picked for it. Left null, the picker drew a new tag with no
        // dot beside the existing ones, which read as a tag that has no colour rather than one
        // that does not exist yet.
        color: ref.color,
        createdAt: '',
      },
  );
  const selectedTags = editedTags ?? proposedTags;

  // Read during render on purpose. `formState` is a Proxy that only computes what has been
  // subscribed to, and reading `isDirty` for the first time inside the submit handler is too
  // late — it comes back `false` however much the user edited, so every apply reported the
  // proposal as accepted verbatim.
  const { isDirty } = form.formState;

  const otherErrors = Object.entries(form.formState.errors).filter(
    (entry): entry is [string, { message?: string }] => entry[0] !== 'title' && !!entry[1],
  );

  return (
    <form
      id="ai-draft-form"
      onSubmit={form.handleSubmit((draft) => onConfirm(draft, isDirty))}
      data-testid="ai-proposal-preview"
      className="rounded-[10px] bg-primary/8 p-4"
    >
      <p className="flex items-center gap-1.5 text-sm font-medium text-primary">
        <Sparkles className="size-4" aria-hidden />
        {t('quickCreate.understoodTitle')}
      </p>

      {/* The title is the one field the design gives its own line: it is what
          the user reads first, so it is directly editable rather than behind a
          chip popover like the metadata. */}
      {/* A textarea, not an input: the title is display text here, and a single-line field
          scrolled a long one out of sight — the value came back whole on confirm, but the user
          could only ever read the first line of it. `field-sizing-content` grows it instead.
          Enter is swallowed so a title stays one line of text however tall the box gets. */}
      <Textarea
        {...form.register('title')}
        rows={1}
        data-testid="ai-proposal-field-title-input"
        aria-label={t('quickCreate.fieldLabels.title')}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.preventDefault();
        }}
        className="mt-3 min-h-0 resize-none border-0 bg-transparent px-0 py-0 text-lg font-semibold shadow-none focus-visible:ring-0 md:text-lg dark:bg-transparent"
      />
      {form.formState.errors.title && (
        <p role="alert" className="mt-1 text-xs text-destructive">
          {t(form.formState.errors.title.message ?? '')}
        </p>
      )}

      {/* Every other field's message, together, above the chips. handleSubmit swallows the
          click when any field fails and only the title rendered its reason, so an invalid
          assignee or date left the confirm button looking broken with nothing on screen. */}
      {otherErrors.length > 0 && (
        <ul data-testid="ai-proposal-errors" role="alert" className="mt-2 text-xs text-destructive">
          {otherErrors.map(([field, error]) => (
            <li key={field}>{t(error.message ?? '')}</li>
          ))}
        </ul>
      )}

      {/* The description gets a labelled field of its own rather than a chip: it is the one
          proposed value too long to read inside a pill, and the one most often corrected. */}
      <label
        htmlFor="ai-proposal-field-description"
        className="mt-3 block text-xs font-medium tracking-wide text-muted-foreground uppercase"
      >
        {t('quickCreate.fieldLabels.description')}
      </label>
      <Textarea
        {...form.register('description')}
        id="ai-proposal-field-description"
        data-testid="ai-proposal-field-description-input"
        rows={5}
        placeholder={t('quickCreate.descriptionPlaceholder')}
        className="mt-1.5 resize-y rounded-[7px] bg-card focus-visible:border-primary"
      />

      <ProposalChipRow
        proposal={proposal}
        control={form.control}
        watch={form.watch}
        options={options}
        projectId={projectId}
        boardId={boardId}
        projectStatuses={projectStatuses}
        selectedTags={selectedTags}
        onTagsChange={setEditedTags}
      />

      {proposal.observations && proposal.observations.length > 0 && (
        <ul className="mt-3 list-disc space-y-1 pl-5 text-xs text-muted-foreground">
          {proposal.observations.map((observation) => (
            <li key={observation}>{observation}</li>
          ))}
        </ul>
      )}

      <ProposalNewEntityWarning proposal={proposal} draft={form.watch()} />
    </form>
  );
}
